import { useEffect, useState } from 'react';
import { Bot, RefreshCw, Save, Sparkles, BookOpen, X } from 'lucide-react';
import toast from 'react-hot-toast';
import { aiApi, type AIModelSetting } from '@/api/ai';

type VendorKey = 'gemini' | 'anthropic' | 'deepseek';

const VENDOR_ORDER: VendorKey[] = ['gemini', 'anthropic', 'deepseek'];
const VENDOR_COLORS: Record<VendorKey, string> = {
  gemini: 'from-blue-600 to-cyan-500',
  anthropic: 'from-orange-500 to-amber-500',
  deepseek: 'from-emerald-500 to-teal-500',
};
export default function AISettingsTab() {
  const [loading, setLoading] = useState(true);
  const [savingModels, setSavingModels] = useState(false);
  const [models, setModels] = useState<AIModelSetting[]>([]);
  const [showInstructionsModal, setShowInstructionsModal] = useState(false);
  const [instructions, setInstructions] = useState('');
  const [savingInstructions, setSavingInstructions] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [config, insRes] = await Promise.all([
        aiApi.getAdminConfig(),
        aiApi.getInstructions().catch(() => ''),
      ]);

      const normalizedModels = VENDOR_ORDER.map((vendor) => {
        const existing = config.models.find((model) => model.vendor === vendor);
        return existing || {
          vendor,
          display_name: vendor,
          default_model: vendor === 'anthropic' ? 'claude-3-5-sonnet-20241022' : vendor === 'deepseek' ? 'deepseek-chat' : 'gemini-3-flash-preview',
          is_active: false,
        };
      });
      const activeCount = normalizedModels.filter((model) => model.is_active).length;
      if (activeCount !== 1 && normalizedModels.length > 0) {
        normalizedModels.forEach((model, index) => {
          model.is_active = index === 0;
        });
      }
      setModels(normalizedModels);
      setInstructions(insRes || '');
    } catch (error) {
      toast.error('AI ayarları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const toggleModel = (vendor: VendorKey) => {
    setModels((prev) => prev.map((model) => ({
      ...model,
      is_active: model.vendor === vendor,
    })));
  };

  const updateModelField = (vendor: VendorKey, value: string) => {
    setModels((prev) => prev.map((model) => (
      model.vendor === vendor ? { ...model, default_model: value } : model
    )));
  };

  const saveModelSettings = async () => {
    const activeCount = models.filter((model) => model.is_active).length;
    if (activeCount !== 1) {
      toast.error('Aynı anda yalnızca tek bir model aktif olmalı.');
      return;
    }

    setSavingModels(true);
    try {
      const result = await aiApi.updateModelSettings(
        models.map((model) => ({
          vendor: model.vendor,
          is_active: model.is_active,
          default_model: model.default_model,
        })),
      );
      setModels(result.models as AIModelSetting[]);
      toast.success('Model ayarları güncellendi.');
    } catch (error: any) {
      toast.error(error?.response?.data?.error || 'Model ayarları kaydedilemedi.');
    } finally {
      setSavingModels(false);
    }
  };

  const saveInstructions = async () => {
    setSavingInstructions(true);
    try {
      await aiApi.updateInstructions(instructions);
      toast.success('AI Talimatları başarıyla güncellendi.');
      setShowInstructionsModal(false);
    } catch (error: any) {
      toast.error('Talimatlar kaydedilemedi.');
    } finally {
      setSavingInstructions(false);
    }
  };

  if (loading) {
    return (
      <div className="flex h-full items-center justify-center rounded-3xl border border-dashed border-slate-200 bg-slate-50/70 p-10">
        <div className="flex items-center gap-3 text-slate-500">
          <RefreshCw size={18} className="animate-spin" />
          <span className="text-sm font-semibold">AI ayarları yükleniyor...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col gap-6">
      <div className="rounded-[28px] border border-slate-200 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-6 text-white shadow-xl">
        <div className="flex items-start justify-between gap-6">
          <div className="max-w-3xl">
            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.25em] text-blue-200/80">
              <Sparkles size={14} />
              AI Ayar Merkezi
            </div>
            <h2 className="mt-3 text-2xl font-black tracking-tight">Vendor bazlı history, context ve model akışı burada yönetiliyor.</h2>
            <p className="mt-3 text-sm leading-relaxed text-slate-300">
              Buradan aktif vendor seçimini tek modele sabitleyebilir, varsayılan model kimliklerini güncelleyebilir
              ve global AI talimatlarını yönetebilirsin.
            </p>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={() => setShowInstructionsModal(true)}
              className="inline-flex items-center gap-2 rounded-2xl bg-white px-4 py-2 text-sm font-bold text-slate-900 transition hover:bg-slate-100 shadow-md"
            >
              <BookOpen size={16} className="text-blue-600" />
              Talimatlar
            </button>
            <button
              onClick={() => void load()}
              className="inline-flex items-center gap-2 rounded-2xl border border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold text-white transition hover:bg-white/15"
            >
              <RefreshCw size={16} />
              Yenile
            </button>
          </div>
        </div>
      </div>

      <section className="rounded-[28px] border border-slate-200 bg-white p-6 shadow-sm">
        <div className="mb-5 flex items-center justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 text-[10px] font-black uppercase tracking-[0.25em] text-slate-400">
              <Bot size={14} className="text-blue-500" />
              Model Yönetimi
            </div>
            <h3 className="mt-2 text-lg font-black text-slate-800">Hangi model aktif kalacak?</h3>
            <p className="mt-1 text-sm text-slate-500">Aynı anda yalnızca tek model aktif olabilir. Aktif model chat ekranında varsayılan olur.</p>
          </div>
          <button
            onClick={() => void saveModelSettings()}
            disabled={savingModels}
            className="inline-flex items-center gap-2 rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-semibold text-white transition hover:bg-emerald-700 disabled:opacity-60"
          >
            <Save size={16} />
            Model Ayarlarını Kaydet
          </button>
        </div>

        <div className="grid gap-4 lg:grid-cols-3">
          {models.map((model) => (
            <div key={model.vendor} className="rounded-[24px] border border-slate-200 bg-slate-50/70 p-5">
              <div className={`inline-flex rounded-2xl bg-gradient-to-r px-3 py-1 text-[10px] font-black uppercase tracking-[0.25em] text-white ${VENDOR_COLORS[model.vendor]}`}>
                {model.display_name}
              </div>
              <div className="mt-4 flex items-center justify-between">
                <div>
                  <div className="text-sm font-bold text-slate-700">Durum</div>
                  <div className="text-xs text-slate-400">{model.is_active ? 'Kullanıcılara açık' : 'Geçici olarak kapalı'}</div>
                </div>
                <button
                  type="button"
                  onClick={() => toggleModel(model.vendor)}
                  className={`relative h-8 w-14 rounded-full transition ${model.is_active ? 'bg-emerald-500' : 'bg-slate-300'}`}
                >
                  <span className={`absolute top-1 h-6 w-6 rounded-full bg-white shadow transition ${model.is_active ? 'left-7' : 'left-1'}`} />
                </button>
              </div>

              <label className="mt-5 block">
                <span className="mb-1 block text-xs font-semibold text-slate-500">Varsayılan Model Kimliği</span>
                <input
                  value={model.default_model}
                  onChange={(event) => updateModelField(model.vendor, event.target.value)}
                  className="w-full rounded-2xl border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 outline-none transition focus:border-blue-400 focus:ring-2 focus:ring-blue-100"
                />
              </label>
            </div>
          ))}
        </div>
      </section>

      {/* Instructions Modal */}
      {showInstructionsModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 backdrop-blur-sm p-4">
          <div className="w-full max-w-3xl rounded-[28px] bg-white shadow-2xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between border-b border-slate-100 p-6">
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-50 text-blue-600">
                  <BookOpen size={20} />
                </div>
                <div>
                  <h3 className="text-lg font-black text-slate-800">Global AI Talimatları</h3>
                  <p className="text-[10px] font-bold tracking-widest uppercase text-slate-400">Model Davranışlarını Yönlendir</p>
                </div>
              </div>
              <button 
                onClick={() => setShowInstructionsModal(false)}
                className="rounded-full p-2 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X size={20} />
              </button>
            </div>
            
            <div className="flex-1 p-6 overflow-y-auto">
              <p className="text-sm text-slate-500 mb-4 leading-relaxed">
                Bu alana yazacağınız talimatlar, mevcut tüm AI hafıza (memory) sistemlerini geçersiz kılar ve 
                bütün yapay zeka modelleri tarafından mutlak kural olarak kabul edilir. Rolünü, dilini, formatını 
                ve kısıtlamalarını buradan belirtebilirsiniz.
              </p>
              <textarea
                value={instructions}
                onChange={(e) => setInstructions(e.target.value)}
                placeholder="Örn: Asla markdown tablo kullanma, tüm cevapları İngilizce ver, kendini uzman mühendis olarak tanıt..."
                className="w-full h-[400px] rounded-2xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700 outline-none transition focus:border-blue-400 focus:bg-white focus:ring-4 focus:ring-blue-100/50 resize-none font-mono"
                spellCheck={false}
              />
            </div>

            <div className="flex items-center justify-end gap-3 border-t border-slate-100 p-6 bg-slate-50/50 rounded-b-[28px]">
              <button
                onClick={() => setShowInstructionsModal(false)}
                className="px-5 py-2.5 text-sm font-bold text-slate-500 hover:text-slate-700 transition"
              >
                İptal
              </button>
              <button
                onClick={saveInstructions}
                disabled={savingInstructions}
                className="inline-flex items-center gap-2 rounded-2xl bg-blue-600 px-6 py-2.5 text-sm font-bold text-white shadow-sm transition hover:bg-blue-700 hover:shadow disabled:opacity-60"
              >
                {savingInstructions ? <RefreshCw size={16} className="animate-spin" /> : <Save size={16} />}
                {savingInstructions ? 'Kaydediliyor...' : 'Talimatları Uygula'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
